import { animate, state, style, transition, trigger } from '@angular/animations';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { safeJsonClone, safeJsonParse } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalService } from '@ebw-monorepo/modal';
import { TranslateService } from '@ngx-translate/core';
import { CustomerEnum } from '../../../../../utils/src/lib/enums/customer.enum';
import { FlatNode, TreeNode } from '../../models/ebw-tree.model';
import { FeatureSelectService } from '../../services/feature-select.service';
import { TelcoService } from '../../services/telco.service';
import { parseUrn } from '../../utils/urn.utils';
@Component({
  selector: 'ebw-monorepo-tree',
  templateUrl: './tree.component.html',
  styleUrls: ['./tree.component.scss'],
  animations: [
    trigger('slideVertical', [
      state(
        '*',
        style({
          height: 0
        })
      ),
      state(
        'show',
        style({
          height: '*'
        })
      ),
      transition('* => *', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')])
    ])
  ]
})
export class TreeComponent implements OnInit {
  isComponentReady = false;

  @Input() set urn(value: string) {
    this._urn = value;
    this.isComponentReady && this.initTree(value);
  }
  _urn = '';

  @Input() type: string;

  @Input() notRecursive = false; // per capire nella prima chiamata di init va recuperata tutta la struttura o soltanto il primo livello
  @Input() expanded = false; // per capire se all'init il tree deve essere di default aperto
  @Input() singleLevel = false; // i figli della radice sono sempre foglie: gss non valorizza is_leaf sui figli di twin_cables_report
  @Input() emptyMessage = '';
  @Input() errorMessage = '';
  isEmpty = false;
  hasError = false;

  @Input()
  set selectable(bool: boolean) {
    this._selectable = bool;
  }
  _selectable = false;

  @Input()
  set closeTreeAfterSelection(bool: boolean) {
    this._closeTreeAfterSelection = bool;
  }
  _closeTreeAfterSelection = true;

  @Input()
  set canStartOtdr(bool: boolean) {
    this._canStartOtdr = bool;
  }
  _canStartOtdr = false;

  @Input()
  set canGoToDetail(bool: boolean) {
    this._canGoToDetail = bool;
  }
  _canGoToDetail = true;

  @Input()
  set canExport(bool: boolean) {
    this._canExport = bool;
  }

  _canExport = true;

  @Input()
  set canShowOnMap(bool: boolean) {
    this._canShowOnMap = bool;
  }

  @Input()
  set canExportConnectivity(bool: boolean) {
    this._canExportConnectivity = bool;
  }
  _canExportConnectivity = false;

  _canShowOnMap = true;

  @Output()
  goToDetail = new EventEmitter<FlatNode>();

  @Output()
  goToRaccordi = new EventEmitter<FlatNode>();

  @Output()
  goToOTDR = new EventEmitter<{ urn: string; name: string }>();

  @Output()
  selectNode = new EventEmitter<{ urn: string; name: string }>();

  treeControl = new NestedTreeControl<TreeNode>((node) => node.children);
  dataSource = new MatTreeNestedDataSource<TreeNode>();

  customerEnum = CustomerEnum;

  // --------------------------------------------------------------------------------------------------------------------

  constructor(
    private _telcoService: TelcoService,
    private _translateService: TranslateService,
    private _ebwModalService: EbwModalService,
    private _featureSelectService: FeatureSelectService
  ) {}

  // --------------------------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.isComponentReady = true;
    this.initTree(this._urn);
  }

  // --------------------------------------------------------------------------------------------------------------------

  initTree = async (urn: string) => {
    if (!urn) {
      console.error('[TreeComponent][initTree] urn mancante');
      return;
    }
    const tree = await this._telcoService.getTreeStructure(urn, this.type, this.notRecursive);
    this.hasError = false;
    if (!!tree) {
      this.isEmpty = !tree.children?.length;
      this.dataSource.data = [tree];
      this.expanded && this.treeControl.expand(tree);
      return;
    }
    console.error('[TreeComponent][initTree] nessun dato trovato');
    this.isEmpty = false;
    this.hasError = true;
    this.dataSource.data = [];
  };

  // --------------------------------------------------------------------------------------------------------------------

  hasChild = (_: number, node: any) => {
    if (!this.notRecursive || this.singleLevel) {
      return !!node.children && node.children.length > 0;
    }
    return !node.is_leaf;
  };

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * Riceve un TreeNode e restituisce un FlatNode,
   * ovvero un oggetto pronto da mostrare
   */
  private nodeTransformer = (node: TreeNode): FlatNode => ({
    name: node.obj_desc,
    type: node.obj_type,
    urn: node.obj_urn,
    isRoot: node.is_root,
    otdrValue: node.obj_value
  });

  // --------------------------------------------------------------------------------------------------------------------

  isAssetSelectableBasedOnType = (node: TreeNode): boolean => this._telcoService.isAssetSelectableBasedOnType(node.obj_type);

  // --------------------------------------------------------------------------------------------------------------------

  isAssetTypesAllowedToStartOtdr = (node: TreeNode): boolean => this._telcoService.isAssetTypesAllowedToStartOtdr(node.obj_type);

  // --------------------------------------------------------------------------------------------------------------------

  isAssetAllowedToDownloadKmzCircuiti = (node: TreeNode): boolean => this._telcoService.isAssetAllowedToDownloadKmzCircuiti(node.obj_urn);

  // --------------------------------------------------------------------------------------------------------------------

  isAssetAllowedToDownloadCsvPercorso = (node: TreeNode): boolean => this._telcoService.isAssetAllowedToDownloadCsvPercorso(node.obj_urn);

  // --------------------------------------------------------------------------------------------------------------------

  isAssetAllowedToViewRaccordi = (node: TreeNode): boolean => this._telcoService.isAssetAllowedToViewRaccordi(node.obj_urn);

  // --------------------------------------------------------------------------------------------------------------------

  isAssetTracciatoIRU = (node: TreeNode) => this._telcoService.isAssetTracciatoIRU(node.obj_urn);

  // --------------------------------------------------------------------------------------------------------------------

  clickToogleButton = async (node: TreeNode, treeControl: NestedTreeControl<TreeNode>): Promise<void> => {
    treeControl.toggle(node);

    if (!this.notRecursive || this.singleLevel) {
      return;
    }

    if (node.children?.length > 0) {
      return;
    }

    const tree = await this._telcoService.getTreeStructure(node.obj_urn, node.used_conf || this.type, this.notRecursive);
    if (!!tree) {
      // assegno solo i children, il resto lo ho già
      node.children = tree.children;
      // sovrascrivo la source del tree, unico modo per triggerare il refresh dell'albero
      this.dataSource.data = safeJsonClone(this.dataSource.data, [...this.dataSource.data]);
      // ricavo la lista di tutti gli oggetti a disposizione nel tree
      const nodeIndex = this.buildIndex(this.dataSource.data);
      // ricavo path dal root al nodo
      const path = this.getPathFromLeafToRoot(node.obj_urn, nodeIndex);
      // espando tutti i nodi del percorso
      for (const node of path) {
        this.treeControl.expand(node);
      }
      // scrollo fino all'elemento desiderato
      setTimeout(() => {
        this.scrollToNode(node.obj_urn);
      }, 500);
      return;
    }
    console.error('[TreeComponent][clickToogleButton] nessun dato trovato');
  };

  // --------------------------------------------------------------------------------------------------------------------

  buildIndex(nodes: TreeNode[], map: Map<string, TreeNode> = new Map()) {
    for (const node of nodes) {
      map.set(node.obj_urn, node);
      if (node.children?.length) {
        this.buildIndex(node.children, map);
      }
    }
    return map;
  }

  // --------------------------------------------------------------------------------------------------------------------

  /**
   *
   * @param startUrn elemento di partenza
   * @param index lista elementi a disposizione
   * @returns lista ordinata degli elementi dal nodo root a quello da cui sono partito
   */
  getPathFromLeafToRoot(startUrn: string, index: Map<string, TreeNode>): TreeNode[] {
    const path: TreeNode[] = [];
    let current = index.get(startUrn);

    while (current) {
      path.push(current);

      if (!current.parent_urn) {
        // è root
        break;
      }

      current = index.get(current.parent_urn);
    }
    // vogliamo root → foglia
    return path.reverse();
  }

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * Per gestire lo scroll dell'elemento di cui sono stati recuperati i nodi figli tramite chiamata con not_recursive
   * @param obj_urn urn elemento
   * @returns {void}
   */
  scrollToNode(obj_urn: string): void {
    const el = document.getElementById(`${obj_urn}`);
    if (!el) return;

    el.scrollIntoView({
      behavior: 'auto',
      block: 'center'
    });
  }

  // --------------------------------------------------------------------------------------------------------------------

  handleButtonAction(action: string, node: TreeNode) {
    const parsedNode = this.nodeTransformer(node);
    const { name, urn, otdrValue } = parsedNode;
    switch (action) {
      case 'select':
        this._closeTreeAfterSelection && this.treeControl.collapseAll();
        const otdrUrn = otdrValue || urn;
        this.selectNode.emit({ urn: otdrUrn, name });
        break;
      case 'detail':
        this.goToDetail.emit(parsedNode);
        break;
      case 'copy':
        this._telcoService.copyToClipboard(parsedNode.name);
        break;
      case 'otdr':
        this.goToOTDR.emit({ urn, name });
        break;
      case 'kmz_circuiti':
        this._telcoService.exportKmz(urn);
        break;
      case 'csv_percorso':
        this._telcoService.downloadExportReportPercorso(urn, 'csv');
        break;
      case 'csv_struttura':
        this._telcoService.downloadTreeStructure(parsedNode, 'csv');
        break;
      case 'html_struttura':
        this._telcoService.downloadTreeStructure(parsedNode, 'html');
        break;
      case 'csv_connectivity':
        this._telcoService.downloadConnectivity(urn, this.type, 'csv');
        break;
      case 'go_to':
        if (this.isAssetTracciatoIRU(node)) {
          this._telcoService.getTracciatoIRUFile(urn, 'kmz').then((file) => file && this._telcoService.showKmzOnMap(file.blob));
        } else {
          this._telcoService.goTo(urn);
        }
        break;
      case 'raccordi':
        this.goToRaccordi.emit(parsedNode);
        break;
      case 'download_kmz_tracciato_olo':
        this._telcoService
          .getTracciatoIRUFile(urn, 'kmz')
          .then((file) => file && this._telcoService.downloadFile(file.blob, `${file.codiceAltroOperatore}.kmz`));
        break;
      case 'download_pdf_tracciato_olo':
        // figure out if it's possible to access the feature properties without calling selectByUrns again
        this._featureSelectService
          .selectByUrns([urn])
          .then((response) => {
            // check that the format of the response fits with what's necessary
            if (
              Array.isArray(response?.result?.features) &&
              response?.result?.features.length >= 1 &&
              Array.isArray(response?.result?.features[0]?.properties?.fw_contract) &&
              response?.result?.features[0]?.properties?.fw_contract.length >= 1
            ) {
              // parse the stringified contract info
              const parsedContract = safeJsonParse(response.result.features[0].properties.fw_contract[0]);

              // extract the urn
              const contractUrn = parsedContract?.urn;
              if (!contractUrn) {
                throw new Error('No contract found');
              }

              // redirect to the contract url
              this.getContract(contractUrn);
            } else {
              throw new Error('No contract found');
            }
          })
          .catch((_error) => {
            const errorTitle = this._translateService.instant('common.error');
            this._ebwModalService.openErrorModal({ title: errorTitle, content: 'Contratto non trovato' });
          });
        // switch from direct pdf download to link to external app
        // this._telcoService
        //   .getTracciatoIRUFile(urn, 'pdf')
        //   .then((file) => file && this._telcoService.downloadFile(file.blob, `${file.codiceAltroOperatore}.pdf`));
        break;
      default:
        console.log('[TreeComponent][handleButtonAction] Unknown action');
        break;
    }
  }

  /**
   * Function copied from report-dialog.component.ts
   * It has frontend components, so might not fit the telco.service.ts
   */
  getContract(urn: string) {
    const id = parseUrn(urn).id;

    this._telcoService.getFwContractsDocumentUrl(id).subscribe({
      next: (res) => {
        if (res && res.error) {
          const errorTitle = this._translateService.instant('common.error');
          if (res.error === 404) {
            this._ebwModalService.openErrorModal({ title: errorTitle, content: 'Contratto non trovato' });
            return;
          }

          if (res.message) {
            this._ebwModalService.openErrorModal({ title: errorTitle, content: res.message });
            return;
          }

          this._ebwModalService.openErrorModal({ title: errorTitle, content: this._translateService.instant('common.no_result') });
        }

        this._ebwModalService
          .openConfirmModal({
            title: this._translateService.instant('map.sidebar.contract.open_contract_modal_title'),
            content: this._translateService.instant('map.sidebar.contract.open_contract_modal_desc')
          })
          .afterClosed()
          .subscribe((result) => {
            if (result == true) {
              window.open(res.result, '_blank');
            }
          });
      },
      error: (error) => {
        const errorTitle = this._translateService.instant('common.error');
        this._ebwModalService.openErrorModal({ title: errorTitle, content: error });
      }
    });
  }
}
